import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getCurrency, getPriceLocale } from './country';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Link mostrati/condivisi agli utenti: sempre sul dominio ufficiale.
export const OFFICIAL_URL = 'https://scanmarket.ch';
export const officialUrl = () => `${OFFICIAL_URL}${window.location.pathname}${window.location.search}`;

// Mappa condizioni DB → chiave i18n per traduzione
const CONDITION_I18N_MAP = {
  'Nuovo': 'condNew',  'Neu': 'condNew',
  'Come nuovo': 'condLikeNew', 'Neuwertig': 'condLikeNew', 'Sehr gut': 'condLikeNew',
  'Buono': 'condGood', 'Gut': 'condGood',
  'Discreto': 'condFair', 'Akzeptabel': 'condFair',
  'Difettoso': 'condBroken', 'Defekt': 'condBroken',
};

export function getTranslatedCondition(condition, t) {
  if (!condition) return '';
  const key = CONDITION_I18N_MAP[condition];
  return key ? t(key) : condition;
}

export function getSessionId() {
  let id = localStorage.getItem('scanmarket_session');
  if (!id) {
    id = 'sess_' + Math.random().toString(36).substr(2, 12);
    localStorage.setItem('scanmarket_session', id);
  }
  return id;
}

// ─── Auth token helpers (fallback per browser che bloccano cookie 3rd-party) ──
const AUTH_TOKEN_KEY = 'scanmarket_auth_token';

export function getAuthToken() {
  try { return localStorage.getItem(AUTH_TOKEN_KEY) || ''; } catch { return ''; }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {}
}

export function clearAuthToken() {
  try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch {}
}

export function authHeaders() {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function formatTimeAgo(dateStr, t) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return `${diff}s ${t('ago')}`;
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} ${m === 1 ? t('minute') : t('minutes')} ${t('ago')}`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} ${h === 1 ? t('hour') : t('hours')} ${t('ago')}`;
  }
  const d = Math.floor(diff / 86400);
  return `${d} ${d === 1 ? t('day') : t('days')} ${t('ago')}`;
}

export function isNew(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return (now - date) < 3600000; // 1 hour
}

export function isRecentlyPosted(listing) {
  // Usa posted_at (data reale marketplace) o first_scraped_at (prima volta nel DB)
  // NON usa scraped_at (aggiornato ogni 5 min → falso positivo)
  const dateStr = listing?.posted_at || listing?.first_scraped_at;
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return (now - date) < 10800000; // 3 ore
}

export function isNewInDb(listing) {
  // Annuncio aggiunto al nostro DB nelle ultime 24h (segnale di freschezza del sito)
  const dateStr = listing?.first_scraped_at;
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return (now - date) < 86400000; // 24h
}

export function formatPrice(price, t) {
  if (price === null || price === undefined) return '';
  if (price === 0) return t ? t('free') : 'Gratis';
  return `${getCurrency()} ${price.toLocaleString(getPriceLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function getSourceBadgeClass(source) {
  const classes = {
    tutti: 'badge-tutti',
    anibis: 'badge-anibis',
    ebay: 'badge-ebay',
    ricardo: 'badge-ricardo',
    facebook: 'badge-facebook',
  };
  return classes[source] || 'badge-tutti';
}

export function getSourceName(source) {
  const names = {
    tutti: 'Tutti.ch',
    anibis: 'Anibis.ch',
    ebay: 'eBay.ch',
    ricardo: 'Ricardo.ch',
    shpock: 'Shpock',
    facebook: 'Facebook',
  };
  return names[source] || source;
}

/**
 * Pulisce il titolo di un annuncio rimuovendo rumore commerciale:
 * "— come nuovo", "TOP Zustand!!!", "(con scatola)", emoji, puntini multipli.
 * Mantiene la sostanza del titolo per migliorare la leggibilità nelle card.
 */
export function cleanTitle(title) {
  if (!title) return '';
  let t = String(title);
  // Rimuovi emoji e simboli decorativi
  t = t.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '');
  // Rimuovi punteggiatura ripetuta (!!! ??? ...)
  t = t.replace(/([!?.])\1{1,}/g, '$1');
  // Frasi commerciali frequenti (IT/DE/FR/EN) — dopo em-dash, pipe o virgola finale
  const noise = [
    /\s*[—\-|,]\s*(come nuovo|ottim[eo] condizioni|top\s*zustand|neuwertig|sehr gut erhalten|etat (neuf|parfait)|like new|mint condition|pari al nuovo|pari nuovo|perfetta|perfetto|con scatola|ovp|originalverpackt|with box|free shipping|gratis versand|livraison gratuite|top zustand|tip top|neuwertig!?|sofort verfügbar)\b.*$/i,
  ];
  for (const r of noise) t = t.replace(r, '');
  // Parentesi finali tipo "(mit OVP)" "(originalverpackt)" "(top)"
  t = t.replace(/\s*\((?:mit\s+)?(?:ovp|originalverpackt|top|neu|nuovo|sealed|new)\)\s*$/i, '');
  // Collassa spazi e rimuovi trailing punctuation residua
  t = t.replace(/\s+/g, ' ').trim();
  t = t.replace(/[\s\-—|,.;:]+$/g, '').trim();
  return t || title;
}

/**
 * Ritorna il livello del deal in base alla reliability e deal_score.
 * Soglie uniformate: <15% neutro, 15-25% ambra (great), >=25% rosso (super).
 */
export function getDealLevel(listing) {
  if (!listing) return null;
  const isAuction = listing.is_auction || listing.sale_type === 'Auktion';
  if (isAuction) return null;
  const reliability = listing.deal_reliability || 'medium';
  if (reliability === 'low') return null;
  const score = listing.deal_score || 0;
  if (score >= 25) return 'super';
  if (score >= 15) return 'great';
  if (score >= 10) return 'good';
  return null;
}

export const TRENDING_SEARCHES = [
  'iPhone', 'MacBook', 'PlayStation 5', 'Samsung TV', 'Dyson',
  'E-Bike', 'Rolex', 'Nintendo Switch', 'AirPods', 'Sofa'
];

export const CATEGORIES = [
  { id: 'all',              label: { de: 'Alle',              fr: 'Tous',              it: 'Tutti',              en: 'All' } },
  { id: 'electronics',      label: { de: 'Elektronik',        fr: 'Electronique',      it: 'Elettronica',        en: 'Electronics' } },
  { id: 'smartphone',       label: { de: 'Smartphones',       fr: 'Smartphones',       it: 'Smartphone',         en: 'Smartphones' } },
  { id: 'computer',         label: { de: 'Computer',          fr: 'Ordinateurs',       it: 'Computer',           en: 'Computers' } },
  { id: 'cars',             label: { de: 'Autos',             fr: 'Voitures',          it: 'Auto',               en: 'Cars' } },
  { id: 'car_accessories',  label: { de: 'Autozubehor',       fr: 'Accessoires auto',  it: 'Accessori auto',     en: 'Car Accessories' } },
  { id: 'bicycles',         label: { de: 'Velos & E-Bikes',   fr: 'Velos & E-Bikes',   it: 'Bici & E-Bike',     en: 'Bikes & E-Bikes' } },
  { id: 'motorcycles',      label: { de: 'Motorrad',          fr: 'Motos',             it: 'Moto',               en: 'Motorcycles' } },
  { id: 'boats',            label: { de: 'Boote',             fr: 'Bateaux',           it: 'Barche',             en: 'Boats' } },
  { id: 'sports',           label: { de: 'Sport',             fr: 'Sport',             it: 'Sport',              en: 'Sports' } },
  { id: 'clothing',         label: { de: 'Kleidung',          fr: 'Vetements',         it: 'Abbigliamento',      en: 'Clothing' } },
  { id: 'furniture',        label: { de: 'Mobel',             fr: 'Meubles',           it: 'Mobili',             en: 'Furniture' } },
  { id: 'watches',          label: { de: 'Uhren',             fr: 'Montres',           it: 'Orologi',            en: 'Watches' } },
  { id: 'games',            label: { de: 'Spiele & Spielzeug', fr: 'Jeux',             it: 'Giochi',             en: 'Games & Toys' } },
  { id: 'books',            label: { de: 'Bucher & Medien',   fr: 'Livres & Medias',   it: 'Libri & Media',      en: 'Books & Media' } },
  { id: 'tools',            label: { de: 'Werkzeug & Garten', fr: 'Outils & Jardin',   it: 'Utensili & Giardino', en: 'Tools & Garden' } },
  { id: 'musical_instruments', label: { de: 'Musikinstrumente', fr: 'Instruments',     it: 'Strumenti musicali', en: 'Musical Instruments' } },
  { id: 'babyKids',         label: { de: 'Baby & Kinder',     fr: 'Bébé & Enfants',    it: 'Bambini & Neonati',  en: 'Baby & Kids' } },
  { id: 'pets',             label: { de: 'Tiere',             fr: 'Animaux',           it: 'Animali',            en: 'Pets' } },
  { id: 'realEstate',       label: { de: 'Immobilien',        fr: 'Immobilier',        it: 'Immobili',           en: 'Real Estate' } },
  { id: 'antiquesArts',     label: { de: 'Antiquitäten & Kunst', fr: 'Antiquités & Art', it: 'Antiquariato & Arte', en: 'Antiques & Art' } },
  { id: 'collectibles',     label: { de: 'Sammeln',           fr: 'Collection',        it: 'Collezionismo',      en: 'Collectibles' } },
  { id: 'tickets',          label: { de: 'Tickets',           fr: 'Billets',           it: 'Biglietti',          en: 'Tickets' } },
];

// Mappa slug grezzi dai marketplace → label umane multilingua.
// Include le 30+ categorie più frequenti in DB (realEstate, babyClothes, ecc.).
// Per slug non mappati, `getCategoryLabel` humanizza la camelCase/snake_case.
const RAW_CATEGORY_LABELS = {
  realEstate:                 { de: 'Immobilien',       fr: 'Immobilier',         it: 'Immobili',              en: 'Real Estate' },
  babyClothes:                { de: 'Babykleidung',     fr: 'Vêtements bébé',     it: 'Abbigliamento bambini', en: 'Baby Clothes' },
  womensClothes:              { de: 'Damenkleidung',    fr: 'Vêtements femme',    it: 'Abbigliamento donna',   en: "Women's Clothes" },
  mensClothes:                { de: 'Herrenkleidung',   fr: 'Vêtements homme',    it: 'Abbigliamento uomo',    en: "Men's Clothes" },
  womensShoes:                { de: 'Damenschuhe',      fr: 'Chaussures femme',   it: 'Scarpe donna',          en: "Women's Shoes" },
  mensShoes:                  { de: 'Herrenschuhe',     fr: 'Chaussures homme',   it: 'Scarpe uomo',           en: "Men's Shoes" },
  equipmentTools:             { de: 'Werkzeug',         fr: 'Outils',             it: 'Attrezzi',              en: 'Tools' },
  gardenOutfitting:           { de: 'Gartenausstattung', fr: 'Équipement jardin', it: 'Arredamento giardino',  en: 'Garden Outfitting' },
  gardenEquipment:            { de: 'Gartengeräte',     fr: 'Matériel jardin',    it: 'Attrezzi giardino',     en: 'Garden Equipment' },
  mobile_phone:               { de: 'Mobiltelefon',     fr: 'Téléphone portable', it: 'Cellulare',             en: 'Mobile Phone' },
  cellPhones:                 { de: 'Mobiltelefone',    fr: 'Téléphones',         it: 'Cellulari',             en: 'Cell Phones' },
  electronic_component:       { de: 'Elektronik',       fr: 'Électronique',       it: 'Elettronica',           en: 'Electronics' },
  computerComponentsAccessories: { de: 'PC-Zubehör',    fr: 'Accessoires PC',     it: 'Accessori PC',          en: 'PC Components' },
  laptop:                     { de: 'Laptop',           fr: 'Ordinateur portable', it: 'Laptop',               en: 'Laptop' },
  television:                 { de: 'Fernseher',        fr: 'Télévision',         it: 'Televisore',            en: 'Television' },
  furniture:                  { de: 'Möbel',            fr: 'Meubles',            it: 'Mobili',                en: 'Furniture' },
  chair:                      { de: 'Stuhl',            fr: 'Chaise',             it: 'Sedia',                 en: 'Chair' },
  lighting:                   { de: 'Beleuchtung',      fr: 'Éclairage',          it: 'Illuminazione',         en: 'Lighting' },
  decorationAccessories:      { de: 'Dekoration',       fr: 'Décoration',         it: 'Decorazione',           en: 'Decoration' },
  childrensRoom:              { de: 'Kinderzimmer',     fr: 'Chambre enfant',     it: 'Camera bambini',        en: "Children's Room" },
  strollersSeats:             { de: 'Kinderwagen',      fr: 'Poussettes',         it: 'Passeggini',            en: 'Strollers' },
  toys:                       { de: 'Spielzeug',        fr: 'Jouets',             it: 'Giocattoli',            en: 'Toys' },
  consolesGames:              { de: 'Konsolen & Games', fr: 'Consoles & Jeux',    it: 'Console e Giochi',      en: 'Consoles & Games' },
  collectibles:               { de: 'Sammlerstücke',    fr: 'Objets de collection', it: 'Collezionismo',       en: 'Collectibles' },
  antiquesArts:               { de: 'Antiquitäten',     fr: 'Antiquités',         it: 'Antiquariato e Arte',   en: 'Antiques & Arts' },
  watchesJewelry:             { de: 'Uhren & Schmuck',  fr: 'Montres & Bijoux',   it: 'Orologi e Gioielli',    en: 'Watches & Jewelry' },
  bagsWallets:                { de: 'Taschen',          fr: 'Sacs',               it: 'Borse',                 en: 'Bags & Wallets' },
  accessories:                { de: 'Accessoires',      fr: 'Accessoires',        it: 'Accessori',             en: 'Accessories' },
  bicycles:                   { de: 'Velos',            fr: 'Vélos',              it: 'Biciclette',            en: 'Bicycles' },
  bicycle:                    { de: 'Velo',             fr: 'Vélo',               it: 'Bicicletta',            en: 'Bicycle' },
  'E-Bikes':                  { de: 'E-Bikes',          fr: 'Vélos électriques',  it: 'E-Bike',                en: 'E-Bikes' },
  cars:                       { de: 'Autos',            fr: 'Voitures',           it: 'Auto',                  en: 'Cars' },
  carAccessories:             { de: 'Autozubehör',      fr: 'Accessoires auto',   it: 'Accessori auto',        en: 'Car Accessories' },
  motorcycleAccessories:      { de: 'Motorradzubehör',  fr: 'Accessoires moto',   it: 'Accessori moto',        en: 'Motorcycle Accessories' },
  otherSports:                { de: 'Andere Sportarten', fr: 'Autres sports',     it: 'Altri sport',           en: 'Other Sports' },
  winterSports:               { de: 'Wintersport',      fr: 'Sports d\u2019hiver', it: 'Sport invernali',      en: 'Winter Sports' },
  fitness:                    { de: 'Fitness',          fr: 'Fitness',            it: 'Fitness',               en: 'Fitness' },
  musicalInstruments:         { de: 'Musikinstrumente', fr: 'Instruments',        it: 'Strumenti musicali',    en: 'Musical Instruments' },
  nonFictionBooks:            { de: 'Sachbücher',       fr: 'Livres essai',       it: 'Saggistica',            en: 'Non-Fiction Books' },
  otherBooks:                 { de: 'Andere Bücher',    fr: 'Autres livres',      it: 'Altri libri',           en: 'Other Books' },
  officeMaterialFurniture:    { de: 'Büromöbel',        fr: 'Mobilier bureau',    it: 'Mobili ufficio',        en: 'Office Furniture' },
  modeling:                   { de: 'Modellbau',        fr: 'Modélisme',          it: 'Modellismo',            en: 'Modeling' },
  radio_controlled_model:     { de: 'RC-Modell',        fr: 'Modèle RC',          it: 'Modello RC',            en: 'RC Model' },
  measuring_instrument:       { de: 'Messgerät',        fr: 'Instrument mesure',  it: 'Strumento misura',      en: 'Measuring Instrument' },
  shoe:                       { de: 'Schuhe',           fr: 'Chaussures',         it: 'Scarpe',                en: 'Shoes' },
  sweater:                    { de: 'Pullover',         fr: 'Pull',               it: 'Maglione',              en: 'Sweater' },
  jacket:                     { de: 'Jacke',            fr: 'Veste',              it: 'Giacca',                en: 'Jacket' },
};

/**
 * getCategoryLabel(slug, lang) — ritorna una label umana per la categoria.
 * 1. Cerca in RAW_CATEGORY_LABELS (slug grezzi marketplace)
 * 2. Cerca in CATEGORIES (slug canonici)
 * 3. Fallback: humanize camelCase/snake_case → "Human Readable"
 */
export function getCategoryLabel(slug, lang = 'it') {
  if (!slug) return '';
  const l = ['de', 'fr', 'it', 'en'].includes(lang) ? lang : 'it';
  const raw = RAW_CATEGORY_LABELS[slug];
  if (raw) return raw[l] || raw.en || slug;
  const canon = CATEGORIES.find(c => c.id === slug);
  if (canon) return canon.label[l] || canon.label.en || slug;
  // Fallback: camelCase/snake_case → "Camel Case"
  return slug
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, c => c.toUpperCase());
}
